/**
 * A flattened, decoupled, and agnostic color palette.
 * Organized by hue and shade (50-950) for a scalable design system.
 */
export const DEFAULT_COLORS = {
  // --- Neutrals (Grayscale) ---
  black: '#000000',
  white: '#FFFFFF',
  // Tailwind gray aliases (used for cp-prefixed parity where needed)
  'gray-50': '#F9FAFB',
  'gray-100': '#F3F4F6',
  'gray-700': '#374151',
  'neutral-50': '#F6F6F6',
  'neutral-100': '#F4F4F4',
  'neutral-200': '#E9E9E9',
  'neutral-300': '#D1D5DB',
  'neutral-400': '#CECED4',
  'neutral-450': '#B0B0B0',
  'neutral-500': '#787391',
  'neutral-600': '#76808B',
  'neutral-700': '#434D52',
  'neutral-900': '#130B40', // Deep Navy functioning as Primary Text/Black
  'neutral-950': '#111111',

  // --- Brand / Primary (Blues) ---
  'blue-50': '#F5F9FF',
  'blue-100': '#EBF5FF',
  'blue-150': '#ECEAFF',
  'blue-175': '#EBE9FF',
  'blue-200': '#DFDBFF',
  'blue-250': '#CDE0FF',
  'blue-300': '#CBD3FF',
  'blue-350': '#79C5FC',
  'blue-400': '#B2BDFF',
  'blue-500': '#0269E1',
  'blue-600': '#4A37FA',
  'blue-700': '#4231E1',
  'blue-800': '#3b2CC8',
  'blue-900': '#1E1679',
  'blue-950': '#0A2342',

  // --- Purple ---
  'purple-200': '#DDCFFF',
  'purple-300': '#B6ACFB',
  'purple-400': '#9879FC',
  'purple-900': '#291398',

  // --- Success (Greens) ---
  'green-50': '#EEF8E8',
  'green-400': '#2CE1B6',
  'green-450': '#6DC27B',
  'green-500': '#68B23E',
  'green-600': '#18AC76',

  // --- Warning (Ambers) ---
  'amber-50': '#FEF7EE',
  'amber-100': '#FADBD0',
  'amber-400': '#FFAB40',
  'amber-450': '#FFB800',
  'amber-500': '#FFA026',

  // --- Danger / Error (Reds) ---
  'red-400': '#F75454',
  'red-500': '#E5113F',
  'red-600': '#DC2626',

  // --- Accent (Corals) ---
  'coral-50': '#FEF4EE',
  'coral-500': '#F56E5C',
  'coral-600': '#DC6161',
  'coral-700': '#C45656',
} as const

export type ColorKey = keyof typeof DEFAULT_COLORS
